import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  IconButton,
  Drawer,
  Box,
  Typography,
  Divider,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as Bell } from "../../assets/images/Bell.svg";
import { useNavigate } from "react-router-dom";
import AuthService from "../../api/AuthService";
import { getTypeMeta, getPriorityMeta, relativeTime } from "../../utils/notificationMeta";

const POLL_INTERVAL = 30000; // 30s — poll-based only, no websocket/push

const extractCount = (res) => {
  const d = res?.data;
  if (typeof d === "number") return d;
  return d?.count ?? d?.unreadCount ?? d?.total ?? 0;
};

// Response envelope: { data: { data: [...], page, limit, totalPages, totalCount } }
const extractList = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  return d?.data ?? [];
};

// "Has more" = page < totalPages.
const hasMorePages = (res, currentPage) => {
  const totalPages = res?.data?.totalPages;
  if (totalPages != null) return currentPage < Number(totalPages);
  return false;
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [tab, setTab] = useState("all"); // all | unread
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const mounted = useRef(true);

  const fetchCount = useCallback(async () => {
    if (!localStorage.getItem("accessToken")) return;
    try {
      const res = await AuthService.getNotificationUnreadCount();
      if (mounted.current && res?.success !== false) setCount(extractCount(res));
    } catch (e) {
      /* silent */
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    fetchCount();
    const id = setInterval(fetchCount, POLL_INTERVAL);
    return () => {
      mounted.current = false;
      clearInterval(id);
    };
  }, [fetchCount]);

  const loadList = useCallback(async (nextTab, nextPage) => {
    const isFirstPage = nextPage === 1;
    if (isFirstPage) setLoading(true);
    else setLoadingMore(true);
    try {
      const res = await AuthService.getNotifications(nextPage, nextTab);
      if (res?.success !== false) {
        const list = extractList(res);
        setItems((prev) => {
          const merged = isFirstPage ? list : [...prev, ...list];
          setHasMore(hasMorePages(res, nextPage));
          return merged;
        });
        setPage(nextPage);
      }
    } catch (e) {
      /* leave list */
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  const handleOpen = () => {
    setOpenDrawer(true);
    loadList(tab, 1);
  };

  const handleTabChange = (_e, newTab) => {
    setTab(newTab);
    setItems([]);
    loadList(newTab, 1);
  };

  const handleItemClick = async (item) => {
    setOpenDrawer(false);
    try {
      await AuthService.getNotificationById(item?._id || item?.id); // marks read
    } catch (e) {
      /* still navigate */
    }
    fetchCount();
    const link = item?.link;
    if (link) {
      if (/^https?:\/\//i.test(link)) window.location.href = link;
      else navigate(link.startsWith("/") ? link : `/${link}`);
    }
  };

  const handleMarkAll = async () => {
    try {
      await AuthService.markAllNotificationsRead();
      setItems((prev) => prev.map((n) => ({ ...n, isRead: true, read: true })));
      setCount(0);
    } catch (e) {
      /* ignore */
    }
  };

  const isUnread = (n) => !(n?.isRead ?? n?.read ?? false);

  return (
    <>
      <IconButton onClick={handleOpen} aria-label="notifications">
        <Badge badgeContent={count} color="error" max={99} overlap="circular" invisible={!count}>
          <Bell />
        </Badge>
      </IconButton>

      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        PaperProps={{ sx: { width: 400, maxWidth: "100vw" } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5 }}>
          <Typography sx={{ fontWeight: 600, fontSize: "16px" }}>Notifications</Typography>
          <Box>
            <Button
              size="small"
              onClick={handleMarkAll}
              disabled={loading || items.length === 0}
              sx={{ textTransform: "none", fontSize: "12px" }}
            >
              Mark all read
            </Button>
            <IconButton size="small" onClick={() => setOpenDrawer(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Tabs value={tab} onChange={handleTabChange} variant="fullWidth" sx={{ minHeight: 40 }}>
          <Tab value="all" label="All" sx={{ textTransform: "none", minHeight: 40 }} />
          <Tab value="unread" label="Unread" sx={{ textTransform: "none", minHeight: 40 }} />
        </Tabs>
        <Divider />

        <Box sx={{ flex: 1, overflowY: "auto" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={22} />
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography sx={{ color: "#878787", fontSize: "13px" }}>
                {tab === "unread" ? "No unread notifications." : "You're all caught up."}
              </Typography>
            </Box>
          ) : (
            <>
              {items.map((item) => {
                const id = item?._id || item?.id;
                const unread = isUnread(item);
                const typeMeta = getTypeMeta(item?.type);
                const priorityMeta = getPriorityMeta(item?.priority);
                const TypeIcon = typeMeta.icon;
                return (
                  <Box
                    key={id}
                    onClick={() => handleItemClick(item)}
                    sx={{
                      px: 2,
                      py: 1.5,
                      cursor: "pointer",
                      display: "flex",
                      gap: 1.25,
                      // Left accent = priority (critical/high stand out).
                      borderLeft: `4px solid ${priorityMeta ? priorityMeta.color : "transparent"}`,
                      backgroundColor: unread ? "rgba(25,118,210,0.06)" : "transparent",
                      borderBottom: "1px solid #f0f0f0",
                      "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
                    }}
                  >
                    <TypeIcon sx={{ color: typeMeta.color, fontSize: 22, mt: "2px", flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        {item?.title && (
                          <Typography
                            sx={{ fontSize: "13px", fontWeight: unread ? 600 : 500, color: "#202020", flex: 1 }}
                          >
                            {item.title}
                          </Typography>
                        )}
                        {priorityMeta && (priorityMeta.label === "Critical" || priorityMeta.label === "High") && (
                          <Chip
                            label={priorityMeta.label}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "10px",
                              color: "#fff",
                              backgroundColor: priorityMeta.color,
                            }}
                          />
                        )}
                      </Box>
                      {(item?.body || item?.message) && (
                        <Typography sx={{ fontSize: "12px", color: "#666", mt: 0.25 }}>
                          {item.body || item.message}
                        </Typography>
                      )}
                      <Typography sx={{ fontSize: "11px", color: "#aaa", mt: 0.5 }}>
                        {relativeTime(item?.createdAt || item?.date)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}

              {hasMore && (
                <Box sx={{ textAlign: "center", py: 1.5 }}>
                  <Button
                    size="small"
                    onClick={() => loadList(tab, page + 1)}
                    disabled={loadingMore}
                    sx={{ textTransform: "none" }}
                  >
                    {loadingMore ? "Loading…" : "Load more"}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default NotificationBell;
